/** Device and capability detection for voice capture. */

export const isMobileDevice = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || ('ontouchstart' in window && window.innerWidth <= 1024)
  );
};

export const isIOS = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

export const isAndroid = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android/i.test(navigator.userAgent);
};

export const isStandalonePwa = (): boolean => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  );
};

export const supportsWebSpeechRecognition = (): boolean => {
  if (typeof window === 'undefined') return false;
  return 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
};

export const supportsMediaRecorderCapture = (): boolean => {
  if (typeof window === 'undefined') return false;
  return typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);
};

const WEB_SPEECH_UNRELIABLE_KEY = 'voiceWebSpeechUnreliable';

/**
 * Mobile browsers always use MediaRecorder + server Whisper transcription.
 * Web Speech is unreliable on iOS and produces endless restart loops on Android
 * (continuous=false sessions end and restart while the UI stays on "Listening…").
 * Desktop keeps Web Speech when available for lower latency.
 */
export const shouldUseMediaRecorderFallback = (): boolean => {
  if (!supportsMediaRecorderCapture()) return false;
  if (isMobileDevice()) return true;
  if (!supportsWebSpeechRecognition()) return true;
  if (isWebSpeechMarkedUnreliable()) return true;
  return false;
};

/** Web Speech failed with a network/service error; prefer server Whisper this session. */
export const markWebSpeechUnreliable = (): void => {
  try {
    sessionStorage.setItem(WEB_SPEECH_UNRELIABLE_KEY, '1');
  } catch {
    /* ignore quota / private mode */
  }
};

export const isWebSpeechMarkedUnreliable = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(WEB_SPEECH_UNRELIABLE_KEY) === '1';
  } catch {
    return false;
  }
};

export const clearWebSpeechUnreliable = (): void => {
  try {
    sessionStorage.removeItem(WEB_SPEECH_UNRELIABLE_KEY);
  } catch {
    /* ignore */
  }
};

export const getPreferredMediaRecorderMimeType = (): string | undefined => {
  if (typeof MediaRecorder === 'undefined') return undefined;
  const types = isIOS()
    ? ['audio/mp4', 'audio/aac', 'audio/webm;codecs=opus', 'audio/webm']
    : ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'];
  return types.find((type) => MediaRecorder.isTypeSupported(type));
};
